import { Select } from '@thanx/uikit/select'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { api } from 'api'
import LookerLabs from 'components/Icons/LookerLabs'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setCurrentOloWebhookPreset } from './mockFetch'
import {
  buildOloWebhookPresets,
  DEFAULT_OLO_WEBHOOK_PRESET_KEY,
  OloWebhookPresetKey,
} from './oloWebhookPresets'

const t = buildTranslate('live_updates.dev_tools')

type StackOption = { value: string; label: string }

export type DevStackToolbarProps = {
  stackOptions: StackOption[]
  selectedStackId: string | null
  onSelectStack: (selected: { value: string }[]) => void
  isSelectedStackOlo: boolean
}

const oloWebhookOptions = buildOloWebhookPresets().map(preset => ({
  value: preset.key,
  label: preset.label,
}))

// Dev-tools-only floating toolbar for the Live Updates scene: installs the
// stack-permutation fetch mock (mockFetch.ts) for the lifetime of the scene,
// and lets the Olo webhook signal state be toggled independently of whichever
// stack is selected (that state is merchant-wide, not per-configuration, so
// it can't live on the stack picker itself). Rendering this at all is gated
// by the caller on a non-production environment AND the ProfileMenu dev tools
// toggle (off by default) -- see index.tsx's `showDevTools`.
const DevStackToolbar = ({
  stackOptions,
  selectedStackId,
  onSelectStack,
  isSelectedStackOlo,
}: DevStackToolbarProps) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const [oloPresetKey, setOloPresetKey] = useState<OloWebhookPresetKey>(
    DEFAULT_OLO_WEBHOOK_PRESET_KEY
  )
  // Deliberately not persisted: a reload is how you get the toolbar back, so
  // it can't get stuck hidden across sessions with no way to restore it.
  const [isHidden, setIsHidden] = useState(false)

  const handleSelectOloPreset = useCallback(
    (selected: { value: OloWebhookPresetKey }[]) => {
      const next = selected[0]?.value
      if (!next || next === oloPresetKey) return
      setOloPresetKey(next)
      setCurrentOloWebhookPreset(next)
      dispatch(
        api.util.invalidateTags([
          'OloWebhookCredential',
          'OrderStatusWebhookReceipts',
        ])
      )
    },
    [oloPresetKey, dispatch]
  )

  // After the hooks, so dismissing doesn't change the hook order — and after
  // the mock is installed, so whichever stack is selected stays applied while
  // the bar is out of the way.
  if (isHidden) return null

  return (
    <div
      data-testid="dev-stack-toolbar"
      className={css({
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '16px',
        padding: '10px 16px',
        backgroundColor: palette.grey90,
        borderTop: `2px solid ${palette.eggYolk}`,
      })}
    >
      <div
        className={css({ display: 'flex', alignItems: 'center', gap: '6px' })}
      >
        <LookerLabs fillColor={palette.eggYolk} />
        <Text.BodyText5
          className={css({ color: `${palette.white} !important` })}
        >
          {t('title')}
        </Text.BodyText5>
      </div>

      <div className={css({ flex: '1 1 260px', maxWidth: '360px' })}>
        <Select
          aria-label={t('stack_aria_label')}
          size="compact"
          options={stackOptions}
          value={stackOptions.filter(
            option => option.value === selectedStackId
          )}
          onChange={onSelectStack}
          clearable={false}
          searchable={false}
        />
      </div>

      <div className={css({ flex: '1 1 260px', maxWidth: '360px' })}>
        {isSelectedStackOlo ? (
          <Select
            aria-label={t('olo_webhook_aria_label')}
            size="compact"
            options={oloWebhookOptions}
            value={oloWebhookOptions.filter(
              option => option.value === oloPresetKey
            )}
            onChange={handleSelectOloPreset}
            clearable={false}
            searchable={false}
          />
        ) : (
          <Text.BodyText5
            className={css({ color: `${palette.grey40} !important` })}
          >
            {t('olo_webhook_not_applicable')}
          </Text.BodyText5>
        )}
      </div>

      <button
        type="button"
        aria-label={t('hide_aria_label')}
        onClick={() => setIsHidden(true)}
        className={css({
          marginLeft: 'auto',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '18px',
          lineHeight: '1',
          padding: '4px 6px',
          color: palette.grey40,
          ':hover': { color: palette.white },
          ':focus-visible': {
            outline: `2px solid ${palette.eggYolk}`,
            outlineOffset: '2px',
            borderRadius: '2px',
          },
        })}
      >
        <i aria-hidden="true" className="fa fa-times" />
      </button>
    </div>
  )
}

export default DevStackToolbar
